import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InstructivoDiagram from './InstructivoDiagram';

// const BASE_URL = 'https://spain-project.pages.dev';
// Cuando migres al dominio final, cambia a:
const BASE_URL = 'http://www.el-barco-esclavista.net';

const SectionLink = ({ to, children }) => (
  <Link
    to={to}
    style={{
      color: 'inherit',
      fontWeight: 'bold',
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      textDecorationColor: 'rgba(255,255,255,0.4)',
    }}
  >
    {children}
  </Link>
);

const handleDownloadPDF = (contentRef, isEn) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  let html = contentRef.current.innerHTML;
  // Convertir los links internos de React Router a links absolutos con el dominio
  html = html.replace(/<a /g, '<a target="_blank" ');
  html = html.replace(/href="\//g, `href="${BASE_URL}/`);

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${isEn ? 'Section Index' : 'Índice de secciones'}</title>
      <style>
        /* Forzar que se impriman los fondos de color en el PDF */
        body {
          font-family: Georgia, 'Times New Roman', serif;
          max-width: 680px;
          margin: 40px auto;
          padding: 20px;
          color: #1a1a1a;
          line-height: 1.8;
          font-size: 14px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        h1 { font-size: 1.5em; margin-bottom: 0.2em; }
        h2 { font-size: 1em; color: #555; font-weight: normal; margin-top: 0; }
        p { margin: 0.8em 0; text-align: justify; }
        a { color: #1a1a1a; font-weight: bold; text-decoration: underline; }
        .note { font-size: 0.85em; color: #666; margin-top: 2em; border-top: 1px solid #ddd; padding-top: 1em; }
        /* Diagrama: centrado, fondo azul que se imprima en el PDF */
        .instructivo-diagram-figure {
          max-width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
          text-align: center !important;
          background: #001c48 !important;
          padding: 1em !important;
          box-sizing: border-box !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .instructivo-diagram-wrap {
          max-width: 100% !important;
          margin-left: auto !important;
          margin-right: auto !important;
          display: inline-block !important;
          line-height: 0;
        }
        .instructivo-diagram-wrap svg {
          max-width: 100% !important;
          height: auto !important;
          display: block !important;
        }
        .print-hint {
          font-size: 12px;
          color: #666;
          margin-bottom: 1em;
          padding: 8px 12px;
          background: #f5f5f5;
          border-radius: 6px;
        }
        @media print {
          .print-hint { display: none !important; }
          body { margin: 20px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          a { text-decoration: underline; }
          .instructivo-diagram-figure {
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
            background: #001c48 !important;
            padding: 1em !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .instructivo-diagram-wrap,
          .instructivo-diagram-wrap svg {
            max-width: 100% !important;
            height: auto !important;
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <p class="print-hint">${isEn ? 'Tip: Enable "Background graphics" in the print dialog so the diagram appears with its blue background in the PDF.' : 'Consejo: Activa "Gráficos de fondo" en el diálogo de impresión para que el diagrama salga con fondo azul en el PDF.'}</p>
      <h1>${isEn ? 'Section Index' : 'Índice de secciones'}</h1>
      ${html}
    </body>
    </html>
  `);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 400);
};

const Instructivo = () => {
  const { t, i18n } = useTranslation();
  const contentRef = useRef(null);
  const isEn = i18n.language === 'en';

  return (
    <div className='TesisPage TesisPage--instructivo'>
      <h1 className='TesisPageTitle'>{t('instructivo.title')}</h1>
      <h2 className='TesisPageSubtitle'>{t('instructivo.subtitle')}</h2>

      <div className='Instructivo-actions'>
        <button
          onClick={() => handleDownloadPDF(contentRef, isEn)}
          style={{
            background: 'rgba(255,255,255,0.12)',
            color: 'inherit',
            border: '1px solid rgba(255,255,255,0.25)',
            padding: '0.5em 1.2em',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9em',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.target.style.background = 'rgba(255,255,255,0.22)'; }}
          onMouseLeave={(e) => { e.target.style.background = 'rgba(255,255,255,0.12)'; }}
        >
          📄 {isEn ? 'Download as PDF' : 'Descargar como PDF'}
        </button>
      </div>

      <article className='TesisPageContent' ref={contentRef}>
        {isEn ? (
          <>
            <p>
              This project brings together a set of research pieces on the slave trade, colonial medicine, and maritime museography. The sections can be read independently or followed in the order presented here.
            </p>
            <p>
              The research begins with <SectionLink to="/Gispert1">Life and health on slave ships</SectionLink>, an introduction that reconstructs the circumstances of the brig <em>Cristina</em>'s arrival in Cuba in 1821, with hundreds of captives aboard, and presents José Gispert, the healer responsible for sanitary matters on this Catalan vessel.
            </p>
            <p>
              The second section, <SectionLink to="/Gispert2">The medical science of the slave trade</SectionLink>, delves into the controversy between Gispert and the doctors of the Junta Sanitaria of Puerto Príncipe, who denied the captives' disembarkation permit upon detecting signs of scurvy, blindness, and syphilis among the enslaved people.
            </p>
            <p>
              From this conflict arises the need to understand <SectionLink to="/Gispert3">Health in the context of the illegalization of the slave trade</SectionLink>, a section that examines how the ban on transatlantic trafficking after 1820 transformed sanitary conditions aboard vessels and reinforced the role of port health institutions in Cuba.
            </p>
            <p>
              Closely linked to the above, <SectionLink to="/Gispert4">Inoculation, vaccine, and globalization</SectionLink> reveals the role of shipboard healers as links connecting the Spanish slave health system with the global transatlantic sanitary system, through the practice of smallpox inoculation and vaccination in African ports before the crossing to America.
            </p>
            <p>
              The section on <SectionLink to="/Gispert5">Diseases of the sea or diseases of the Atlantic slave trade</SectionLink> addresses the medical conditions suffered by captives during the Atlantic crossing: scurvy, night blindness, skin diseases, and other pathologies derived from avitaminosis, overcrowding, and prolonged confinement under the ships' decks.
            </p>
            <p>
              Finally, <SectionLink to="/Gispert6">The slave ship and necropower</SectionLink> offers a theoretical framework that connects the analysis of slave medicine with concepts of necropolitics and racial capitalism, proposing a decolonial reading of the medical body in the context of the slave trade.
            </p>
            <p>
              The project also includes two sections on maritime museography: <SectionLink to="/Museo1">The absent ship</SectionLink> and <SectionLink to="/Museo2">Cultural capitalism and silence</SectionLink>, which examine how maritime museums in the Mediterranean region have addressed—or silenced—the colonial and slave-trading past.
            </p>
            <p>
              To learn more about the author and the project, visit the credits at the bottom of the <SectionLink to="/">home page</SectionLink>. If you wish to receive updates, you can register through the <SectionLink to="/VisitorRegistration">Visitor registration</SectionLink> form.
            </p>
            <InstructivoDiagram />
            <p className="note" style={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.9em', marginTop: '2em', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '1em' }}>
              This index is a working document. The content of some sections may be updated as the research progresses.
            </p>
          </>
        ) : (
          <>
            <p>
              Este proyecto reúne un conjunto de piezas de investigación sobre la trata de esclavos, la medicina colonial y la museografía marítima. Las secciones pueden leerse de forma independiente o seguirse en el orden que aquí se presenta.
            </p>
            <p>
              La investigación comienza con <SectionLink to="/Gispert1">La vida y la salud en los barcos esclavistas</SectionLink>, una introducción que reconstruye las circunstancias de la llegada del bergantín <em>Cristina</em> a Cuba en 1821, con cientos de cautivos a bordo, y presenta a José Gispert, el sanador responsable de los asuntos sanitarios de esta embarcación catalana.
            </p>
            <p>
              La segunda sección, <SectionLink to="/Gispert2">La ciencia médica de la trata de esclavos</SectionLink>, profundiza en la controversia entre Gispert y los médicos de la Junta Sanitaria de Puerto Príncipe, quienes denegaron el permiso de desembarco de los cautivos al detectar signos de escorbuto, ceguera y sífilis entre las personas esclavizadas.
            </p>
            <p>
              De este conflicto surge la necesidad de comprender <SectionLink to="/Gispert3">La sanidad en el marco de la ilegalización de la trata de esclavos</SectionLink>, sección que examina cómo la prohibición de la trata trasatlántica a partir de 1820 transformó las condiciones sanitarias a bordo de las embarcaciones y reforzó el papel de las instituciones de salud portuarias en Cuba.
            </p>
            <p>
              Estrechamente vinculada a lo anterior, <SectionLink to="/Gispert4">Inoculación, vacuna y globalización</SectionLink> revela el papel de los sanadores de a bordo como los eslabones que conectaron el sistema sanitario del esclavismo español con el sistema sanitario global transatlántico, a través de la práctica de la inoculación y la vacunación contra la viruela en los puertos africanos antes de emprender el cruce hacia América.
            </p>
            <p>
              La sección sobre <SectionLink to="/Gispert5">Enfermedades del mar o enfermedades de la trata atlántica</SectionLink> aborda las afecciones médicas padecidas por los cautivos durante la travesía atlántica: el escorbuto, la ceguera nocturna, las enfermedades cutáneas y otras patologías derivadas de la avitaminosis, el hacinamiento y el confinamiento prolongado bajo las cubiertas de las naves.
            </p>
            <p>
              Finalmente, <SectionLink to="/Gispert6">El barco de esclavos y el necropoder</SectionLink> ofrece un marco teórico que conecta el análisis de la medicina esclavista con los conceptos de necropolítica y capitalismo racial, proponiendo una lectura decolonial del cuerpo médico en el contexto de la trata de esclavos.
            </p>
            <p>
              El proyecto incluye también dos secciones sobre museografía marítima: <SectionLink to="/Museo1">El barco ausente</SectionLink> y <SectionLink to="/Museo2">Capitalismo cultural y silencio</SectionLink>, que examinan cómo los museos marítimos de la región mediterránea han abordado —o silenciado— el pasado colonial y esclavista.
            </p>
            <p>
              Para conocer más sobre la autora y el proyecto, visita los créditos en la <SectionLink to="/">página de inicio</SectionLink>. Si deseas recibir actualizaciones, puedes registrarte a través del formulario de <SectionLink to="/VisitorRegistration">Registro de visitantes</SectionLink>.
            </p>
            <InstructivoDiagram />
            <p className="note" style={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.9em', marginTop: '2em', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '1em' }}>
              Este índice es un documento de trabajo. El contenido de algunas secciones podrá actualizarse a medida que avance la investigación.
            </p>
          </>
        )}
      </article>
    </div>
  );
};

export default Instructivo;
